import { Injectable } from '@angular/core';
import { Uuid } from '@lib/shared-interface-utility-types';
import { firstValueFrom, map, Observable, shareReplay } from 'rxjs';
import {
    NewSchoolCustomerInput,
    SchoolCustomer,
    SchoolCustomerUpdate,
} from '@lib/shared-interface-school';
import {
    ApolloAdminFindSchoolCustomerAndAccountsByIdGQL,
    ApolloCreateSchoolCustomerGQL,
    ApolloDeleteSchoolCustomerGQL,
    ApolloFindSchoolCustomerAndAccountsByIdGQL,
    ApolloFindSchoolCustomerByIdGQL,
    ApolloFindStudentSchoolByIdGQL,
    ApolloGetUserSchoolAndAccountsGQL,
    ApolloSearchSchoolCustomersGQL,
    ApolloSearchStudentSchoolsGQL,
    ApolloUpdateSchoolCustomerGQL,
    handleMutationResult,
    handleQueryResult,
} from '@lib/frontend-shared-data-gql-operations';

@Injectable({
    providedIn: 'root',
})
export class SchoolCustomersService {
    public readonly userSchool$: Observable<SchoolCustomer | undefined> =
        this.getUserSchoolAndAccountsGql.watch().valueChanges.pipe(
            map((result) => result.data.userSchool),
            shareReplay(1),
        );

    public constructor(
        private readonly createSchoolCustomerGql: ApolloCreateSchoolCustomerGQL,
        private readonly deleteSchoolCustomerGql: ApolloDeleteSchoolCustomerGQL,
        private readonly findSchoolCustomerAndAccountsByIdGql: ApolloFindSchoolCustomerAndAccountsByIdGQL,
        private readonly adminFindSchoolCustomerAndAccountsByIdGql: ApolloAdminFindSchoolCustomerAndAccountsByIdGQL,
        private readonly findSchoolCustomerByIdGql: ApolloFindSchoolCustomerByIdGQL,
        private readonly findStudentSchoolByIdGql: ApolloFindStudentSchoolByIdGQL,
        private readonly getUserSchoolAndAccountsGql: ApolloGetUserSchoolAndAccountsGQL,
        private readonly searchSchoolCustomersGql: ApolloSearchSchoolCustomersGQL,
        private readonly searchStudentSchoolsGql: ApolloSearchStudentSchoolsGQL,
        private readonly updateSchoolCustomerGql: ApolloUpdateSchoolCustomerGQL,
    ) {}

    public createCustomer(input: NewSchoolCustomerInput): Promise<SchoolCustomer> {
        const result$ = this.createSchoolCustomerGql.mutate({ input });
        const schoolCustomer$ = result$.pipe(
            handleMutationResult(),
            map((data) => data.createSchoolCustomer),
        );

        return firstValueFrom(schoolCustomer$);
    }

    public deleteById(id: Uuid): Promise<Uuid> {
        const result$ = this.deleteSchoolCustomerGql.mutate({ id }, { refetchQueries: 'active' });
        const deletedId$ = result$.pipe(
            handleMutationResult(),
            map((data) => data.deleteSchoolCustomer.id),
        );

        return firstValueFrom(deletedId$);
    }

    public findStudentSchoolById(id: Uuid): Observable<StudentSchool | undefined> {
        const query = this.findStudentSchoolByIdGql.watch({ id });
        return query.valueChanges.pipe(
            handleQueryResult(),
            map((data) => data.studentSchool),
        );
    }

    public watchById(id: Uuid): Observable<SchoolCustomer | undefined> {
        const query = this.findSchoolCustomerByIdGql.watch({ id });
        return query.valueChanges.pipe(
            handleQueryResult(),
            map((data) => data.schoolCustomer),
        );
    }

    public watchWithAccountsById(id: Uuid): Observable<SchoolCustomer | undefined> {
        const query = this.findSchoolCustomerAndAccountsByIdGql.watch({ id });
        return query.valueChanges.pipe(
            handleQueryResult(),
            map((data) => data.schoolCustomer),
        );
    }

    public watchAdminSchoolCustomerAndAccounts(id: Uuid): Observable<SchoolCustomer> {
        const query = this.adminFindSchoolCustomerAndAccountsByIdGql.watch({ id });
        return query.valueChanges.pipe(
            handleQueryResult(),
            map((data) => data.adminSchoolCustomer),
        );
    }

    public updateById(id: Uuid, update: SchoolCustomerUpdate): Promise<SchoolCustomer> {
        const result$ = this.updateSchoolCustomerGql.mutate({ id, update });
        const schoolCustomer$ = result$.pipe(
            handleMutationResult(),
            map((data) => data.updateSchoolCustomer),
        );

        return firstValueFrom(schoolCustomer$);
    }

    public watchSearch(searchTerm: string): Observable<SchoolCustomer[]> {
        const searchCriteria = { searchTerm, limit: 1000 };
        const query = this.searchSchoolCustomersGql.watch({ searchCriteria });
        return query.valueChanges.pipe(
            handleQueryResult(),
            map((data) => data.schoolCustomers),
        );
    }

    public watchStudentSchoolsSearch(searchTerm: string): Observable<StudentSchool[]> {
        const searchCriteria = { searchTerm, limit: 100 };
        const query = this.searchStudentSchoolsGql.watch({ searchCriteria });
        return query.valueChanges.pipe(
            handleQueryResult(),
            map((data) => data.studentSchools),
        );
    }
}

export type StudentSchool = Pick<SchoolCustomer, 'id' | 'name' | 'phone' | 'website' | 'location'>;
